/**
 * App.tsx · 09_APP · OS-006
 *
 * Auth gate and shell. Nothing reaches the database until Supabase says there
 * is a session, because every os_ verb is security invoker and an unsigned
 * caller has no auth.uid() and therefore no business here.
 *
 * 2026-10-15: the navy side rail is replaced by a white top bar carrying the
 * Connect2Change logo (the 2026-09-18 master with the floating shadow), per the
 * Today design Emily approved that day. Brand standard: the shadow is part of the logo.
 */

import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase, configProblem, isConfigured, signOut } from "./lib/supabase";
import { SignIn } from "./components/SignIn";
import { Today } from "./components/Today";
import { Settings } from "./components/Settings";
import { getTheme, type Theme } from "./lib/theme";
import { Email } from "./components/Email";
import { People } from "./components/People";
import { AskClaude, setAskContext } from "./components/AskClaude";

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  // 2026-10-19: Today is the dashboard; Email is its own tab (C12b, C41). The hash keeps the tab on reload.
  // 2026-10-20: Settings tab added (D-0072). People tab added (C43).
  type Tab = "today" | "people" | "email" | "settings";
  const [tab, setTab] = useState<Tab>(() =>
    location.hash === "#email" ? "email" : location.hash === "#settings" ? "settings"
    : location.hash.startsWith("#people") ? "people" : "today");
  function go(t: Tab) { setTab(t); history.replaceState(null, "", t === "today" ? "#" : "#" + t); }
  // C44: a "Next steps due" link on Today is #people/<id>, which opens that profile.
  const [openId, setOpenId] = useState<string | null>(() => location.hash.match(/^#people\/([0-9a-f-]{36})$/)?.[1] ?? null);
  useEffect(() => {
    const on = () => { const m = location.hash.match(/^#people\/([0-9a-f-]{36})$/); if (m) { setOpenId(m[1]!); setTab("people"); } };
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  useEffect(() => { setAskContext({ page: tab, personId: null, personName: null }); }, [tab]);
  const [theme, setThemeState] = useState<Theme>(getTheme());
  useEffect(() => {
    const on = (e: Event) => setThemeState((e as CustomEvent<Theme>).detail);
    window.addEventListener("c2c-theme", on);
    return () => window.removeEventListener("c2c-theme", on);
  }, []);

  useEffect(() => {
    if (!isConfigured) { setReady(true); return; }
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (!ready) return <div className="signin"><div className="box muted">Checking your session…</div></div>;
  if (!isConfigured) return <SignIn problem={configProblem} />;
  if (!session) return <SignIn />;

  return (
    <div className="wrap-os">
      <header className="topbar">
        <img src={theme === "dark" ? "/brand/C2C_logo_header_dark.png" : "/brand/C2C_logo_header.png"} alt="Connect2Change" />
        <div className="who">
          Relationship OS
          <br />
          {session.user.email} · <button onClick={() => void signOut()}>Sign out</button>
        </div>
      </header>
      <nav className="ostabs" aria-label="Sections">
        <button className={tab === "today" ? "on" : ""} aria-current={tab === "today" ? "page" : undefined} onClick={() => go("today")}>Today</button>
        <button className={tab === "people" ? "on" : ""} aria-current={tab === "people" ? "page" : undefined} onClick={() => go("people")}>People</button>
        <button className={tab === "email" ? "on" : ""} aria-current={tab === "email" ? "page" : undefined} onClick={() => go("email")}>Email</button>
        <button className={tab === "settings" ? "on" : ""} aria-current={tab === "settings" ? "page" : undefined} onClick={() => go("settings")}>Settings</button>
      </nav>
      {/* C45: Ask Claude on every page. Today carries it inside its own design, so it is not doubled there. */}
      {tab !== "today" && <AskClaude />}
      {tab === "today" ? <Today /> : tab === "people" ? <People initialOpen={openId} onClosed={() => setOpenId(null)} /> : tab === "email" ? <Email /> : <Settings />}
    </div>
  );
}
